import { Injectable } from '@angular/core';

import { Subject } from 'rxjs';

import { EnableClassTelemetry } from 'app/services/otel.service';
import {
  ConceptTopicItem,
  ConfigurationItem,
  QueryTopicItem,
  UserEntityItem,
} from '../../../providers/semantria-api/configuration.service';

export interface TargetEmitEvent<T extends ConfigurationItem> {
  event: 'ADD' | 'REMOVE' | 'REMOVE_ALL' | 'MODIFY';
  targetType: 'CONCEPT_TOPIC' | 'QUERY_TOPIC' | 'USER_ENTITY';
  target: T | T[];
  inverseTarget?: string[];
}

@EnableClassTelemetry()
@Injectable()
export class TaxonomyTargetWatcherService {
  private targetEmitter$ = new Subject<TargetEmitEvent<ConfigurationItem>>();
  onTargetEvent = this.targetEmitter$.asObservable();

  constructor() {}

  public conceptTopicAdded(item: ConceptTopicItem) {
    this.targetEmitter$.next({
      event: 'ADD',
      targetType: 'CONCEPT_TOPIC',
      target: item,
    });
  }

  public conceptTopicModified(item: ConceptTopicItem) {
    this.targetEmitter$.next({
      event: 'MODIFY',
      targetType: 'CONCEPT_TOPIC',
      target: item,
    });
  }

  public conceptTopicDeleted(item: ConceptTopicItem) {
    this.targetEmitter$.next({
      event: 'REMOVE',
      targetType: 'CONCEPT_TOPIC',
      target: item,
    });
  }

  public conceptTopicsDeleted(items: ConceptTopicItem[]) {
    this.targetEmitter$.next({
      event: 'REMOVE',
      targetType: 'CONCEPT_TOPIC',
      target: items,
    });
  }

  public allConceptTopicsDeleted(itemIds: string[]) {
    this.targetEmitter$.next({
      event: 'REMOVE_ALL',
      targetType: 'CONCEPT_TOPIC',
      target: [],
      inverseTarget: itemIds,
    });
  }

  public queryTopicAdded(item: QueryTopicItem) {
    this.targetEmitter$.next({
      event: 'ADD',
      targetType: 'QUERY_TOPIC',
      target: item,
    });
  }

  public queryTopicModified(item: QueryTopicItem) {
    this.targetEmitter$.next({
      event: 'MODIFY',
      targetType: 'QUERY_TOPIC',
      target: item,
    });
  }

  public queryTopicDeleted(item: QueryTopicItem) {
    this.targetEmitter$.next({
      event: 'REMOVE',
      targetType: 'QUERY_TOPIC',
      target: item,
    });
  }

  public queryTopicsDeleted(items: QueryTopicItem[]) {
    this.targetEmitter$.next({
      event: 'REMOVE',
      targetType: 'QUERY_TOPIC',
      target: items,
    });
  }

  public allQueryTopicsDeleted(itemIds: string[]) {
    this.targetEmitter$.next({
      event: 'REMOVE_ALL',
      targetType: 'QUERY_TOPIC',
      target: [],
      inverseTarget: itemIds,
    });
  }

  public entityAdded(item: UserEntityItem) {
    this.targetEmitter$.next({
      event: 'ADD',
      targetType: 'USER_ENTITY',
      target: item,
    });
  }

  public entityModified(item: UserEntityItem) {
    this.targetEmitter$.next({
      event: 'MODIFY',
      targetType: 'USER_ENTITY',
      target: item,
    });
  }

  public entityDeleted(item: UserEntityItem) {
    this.targetEmitter$.next({
      event: 'REMOVE',
      targetType: 'USER_ENTITY',
      target: item,
    });
  }

  public entitiesDeleted(items: UserEntityItem[]) {
    this.targetEmitter$.next({
      event: 'REMOVE',
      targetType: 'USER_ENTITY',
      target: items,
    });
  }

  public allEntitiesDeleted(itemIds: string[]) {
    this.targetEmitter$.next({
      event: 'REMOVE_ALL',
      targetType: 'USER_ENTITY',
      target: [],
      inverseTarget: itemIds,
    });
  }
}
